/**
 * Finish regions follow the cut.
 *
 * A FLOOR region is a rectangle stored as percentages of the floor's bounding
 * box — `x`/`width` along X from its min, `y`/`height` along Z from its min
 * (`web-app/src/utils/fixtureTextureRegionUtils.js: calculateShowerFloorRegion`,
 * read back by the take-off's `regionRectOnFloor`). A WALL region is a
 * percentage along the wall from `bottomLeft` (`shared-lib/millwork/regions.js`),
 * with `y`/`height` up the wall.
 *
 * Copied unchanged onto a smaller piece, a partial region (a shower pan, a band
 * of tile) is stretched over the wrong area and billed in both rooms. So each
 * region goes to world coordinates, is clipped to the piece, and comes back as
 * a percentage of the piece; a region with nothing left on a piece is dropped
 * from it. Every other field (ids, `scanned`, materials, fixture links) rides
 * along untouched.
 *
 * A floor region is clipped to the bounding box of its overlap with the piece
 * POLYGON, not to the piece's bounding box: an L-shaped kept piece has the
 * whole room as its bounding box, and a shower pan inside the closet carved off
 * its corner would otherwise stay on the kept floor too and bill twice.
 *
 * Pure.
 */

const {
  clipPolygonByHalfPlane,
  polygonArea,
} = require('../split-level/raisedFootprint');

/** Below this, a clipped region is empty (m² for floors, a fraction for walls). */
const EMPTY_EPS = 1e-9;

function bboxOf(poly) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minZ, maxZ, W: maxX - minX, D: maxZ - minZ };
}

/** `poly` ∩ the axis-aligned rect, by four half-plane clips. */
function clipToRect(poly, r) {
  let out = poly;
  out = clipPolygonByHalfPlane(out, { x: 1, z: 0 }, r.x0);
  out = clipPolygonByHalfPlane(out, { x: -1, z: 0 }, -r.x1);
  out = clipPolygonByHalfPlane(out, { x: 0, z: 1 }, r.z0);
  out = clipPolygonByHalfPlane(out, { x: 0, z: -1 }, -r.z1);
  return out;
}

/** A floor region re-expressed on `piece`, or null when none of it is there. */
function clipFloorRegion(region, src, piece, pieceBox) {
  const x = region.x ?? 0;
  const y = region.y ?? 0;
  const w = region.width ?? 100;
  const hgt = region.height ?? 100;
  const rect = {
    x0: src.minX + (x / 100) * src.W,
    x1: src.minX + ((x + w) / 100) * src.W,
    z0: src.minZ + (y / 100) * src.D,
    z1: src.minZ + ((y + hgt) / 100) * src.D,
  };
  const overlap = clipToRect(piece, rect);
  if (overlap.length < 3 || polygonArea(overlap) <= EMPTY_EPS) return null;
  const b = bboxOf(overlap);
  if (!(pieceBox.W > 0) || !(pieceBox.D > 0)) return null;
  return {
    ...region,
    x: ((b.minX - pieceBox.minX) / pieceBox.W) * 100,
    y: ((b.minZ - pieceBox.minZ) / pieceBox.D) * 100,
    width: (b.W / pieceBox.W) * 100,
    height: (b.D / pieceBox.D) * 100,
  };
}

/**
 * A floor texture re-mapped from the source floor onto one piece of it.
 *
 * @param {object|undefined} texture the source floor's `texture`
 * @param {{x,z}[]} sourcePolygon the source floor's outline
 * @param {{x,z}[]} piecePolygon the piece's outline
 * @returns {object|undefined} a new texture; undefined when there was none
 */
function clipFloorTexture(texture, sourcePolygon, piecePolygon) {
  if (!texture) return undefined;
  if (!Array.isArray(texture.regions)) return { ...texture };
  const src = bboxOf(sourcePolygon);
  const pieceBox = bboxOf(piecePolygon);
  const regions = texture.regions
    .map((r) => (r ? clipFloorRegion(r, src, piecePolygon, pieceBox) : null))
    .filter(Boolean);
  return { ...texture, regions };
}

/**
 * A wall texture re-mapped onto the piece `[s0, s1]` of its wall (fractions of
 * `bottomLeft → bottomRight`, the `clipWall` convention). Only `x`/`width`
 * change; `y`/`height` run up the wall and are untouched.
 *
 * @returns {object|undefined} a new texture; undefined when there was none
 */
function clipWallTextureRegions(texture, s0, s1) {
  if (!texture) return undefined;
  if (!Array.isArray(texture.regions)) return { ...texture };
  const span = s1 - s0;
  const regions = [];
  for (const r of texture.regions) {
    if (!r) continue;
    if (!(span > EMPTY_EPS)) continue;
    const a = (r.x ?? 0) / 100;
    const b = a + (r.width ?? 100) / 100;
    const lo = Math.max(a, s0);
    const hi = Math.min(b, s1);
    if (hi - lo <= EMPTY_EPS) continue;
    regions.push({
      ...r,
      x: ((lo - s0) / span) * 100,
      width: ((hi - lo) / span) * 100,
    });
  }
  return { ...texture, regions };
}

module.exports = { clipFloorTexture, clipWallTextureRegions };
